<script>
    //import {roi, finalFdkSettings} from "../components/Preview.svelte";
    import { roi, finalFdkSettings } from "../previewstores.js";
    import RangeSlider from "svelte-range-slider-pips";
    import Switch from "../Switch.svelte";
    import { onMount } from "svelte";
    import { getScanThumbnailUrl, isNumeric } from "../utils.js";

    export let tomography;
    export let currentPreviewType = "CENTERSHIFT";
    export let centershiftSlices;
    export let beamhardCuppingSlices;
    export let sliceIndex;
    export let tmpSliceIndex;

    $: tmpSliceIndex = [sliceIndex]; // RangeSlider wants an array

    export let step = 1;
    export let snap; // Keep undefined if you don't want a snap widget
    export let crop = false; // Crop the image height to roi:ed slices. If false, the entire proj image is shown and fogs are enabled.
    let showSnap;
    let img;
    let imgHeight = 256;
    let imgWidth = 256;
    let cameraImageWidth = 2048;
    let showFullImage = !crop;

    // Scale a slice index, e.g. to draw a line on the overview image in the slice pane
    function scaleSliceIndex(sliceIndex) {
        const percent = (sliceIndex - $roi.top) / ($roi.bot - $roi.top + 1);
        const pixel = percent * imgHeight;
        return pixel;
    }

    function onSelectedSlice(value) {
        if (!snap) {
            console.log("SLICEPICKER SELECTED VALUE: ", value);
            sliceIndex = value;
            tmpSliceIndex = [sliceIndex];
        } else {
            let nearestSlice = getNearestMatchingSlice(value);
            console.log(
                "SLICEPICKER SELECTED VALUE ",
                value,
                ", NEAREST SLICE: ",
                nearestSlice,
                " (",
                currentPreviewType,
                " )"
            );
            sliceIndex = nearestSlice;
            tmpSliceIndex = [sliceIndex];
        }
    }

    function getNearestMatchingSlice(value) {
        let availableSlices;
        switch (currentPreviewType) {
            case "CENTERSHIFT":
                availableSlices = centershiftSlices;
                break;
            case "BEAMHARD_CUPPING":
                availableSlices = beamhardCuppingSlices;
                break;
            case "PHASE_RETRIEVAL":
                availableSlices = centershiftSlices;
                break;
            case "FINAL":
                if (beamhardCuppingSlices && beamhardCuppingSlices?.length > 0) {
                    availableSlices = beamhardCuppingSlices;
                } else {
                    availableSlices = centershiftSlices; 
                }
                break;

            default:
                break;
        }

        if (availableSlices?.length > 0) {
            const closestSlice = availableSlices?.reduce((a, b) => {
                return Math.abs(b - value) < Math.abs(a - value) ? b : a;
            });
            return closestSlice;
        } else if (!beamhardCuppingSlices || beamhardCuppingSlices?.length == 0) {
            // Special handling for when there are no beamhard cupping slices:
            // Instead of defaulting to undefined, give the nearest existing centershift slice
            const closestSlice = centershiftSlices?.reduce((a, b) => {
                return Math.abs(b - value) < Math.abs(a - value) ? b : a;
            });
            return closestSlice;
        }

        return undefined;
    }

    function handleImageLoad(ev) {
        // DEBUG: console.log('SLICEPICKER IMAGE LOAD', ev);
        imgWidth = ev.srcElement.naturalWidth;

        imgHeight = calculateImageHeight();
    }

    function handleImageLoadError() {
        // DEBUG: console.log('SLICEPICKER IMAGE ERROR', ev);
        imgWidth = 256;

        imgHeight = calculateImageHeight();
    }

    // Use metadata from camera and the reconstructed image to calculate
    // how tall the image should be in order to preserve the aspect ratio
    // and make sure the slice selection matches the correct vertical position
    // of the image.
    function calculateImageHeight() {
        const cameraRoi = tomography?.command?.camera?.roi_px;
        const roiImageHeight = Math.abs(cameraRoi?.bot - cameraRoi?.top + 1);
        const roiImageWidth = Math.abs(cameraRoi?.right - cameraRoi?.left + 1);
        cameraImageWidth = roiImageWidth;

        // If the image were a square, then the width would also be the full height.
        // Therefore, the image can be scaled as percentage of the width.
        const imgViewportHeightPercent = ($roi.bot - $roi.top + 1) / roiImageWidth || 1;
        let tmpImgHeight = imgViewportHeightPercent * imgWidth;

        // If the image is too 'thin' in height, the range slider doesn't render nicely
        // and it is hard to us it. Thus, use a minimum height, even if it means
        // that the slice selection doesn't quite match the image.
        if (tmpImgHeight < 20) {
            tmpImgHeight = 20;
        }
        return tmpImgHeight;
    }

    function selectSlice(event) {
        if (!isNumeric(sliceIndex)) return; // Ignore if not initialised
        const maxSlices = $roi?.bot - $roi?.top;
        const percentFromTop = event.layerY/imgHeight;
        const clickedslice = Math.round(percentFromTop * maxSlices / step) * step;
        onSelectedSlice(clickedslice);
    }

    // Change slice picker when slider stops or a moment after it changed last
    let isSliceSliderMoving = false;
    let timeout;
    async function onSliceSliderChange(ev) {
        console.log("SLIDER=", ev.detail.value, " (", sliceIndex, ")");
        isSliceSliderMoving = true;
        clearTimeout(timeout);

        timeout = setTimeout(() => {
            isSliceSliderMoving = false;
            console.log("STOPPED SLIDER=", ev.detail.value, " (", sliceIndex, ")");
            tmpSliceIndex = [ev.detail.value];
            onSelectedSlice(ev.detail.value);
        }, 500);
    }

    async function onSliceSliderStop(ev) {
        console.log("STOPPED SLIDER=", ev.detail.value, " (", sliceIndex, ")");
        isSliceSliderMoving = false;
        clearTimeout(timeout);

        tmpSliceIndex = [ev.detail.value];
        onSelectedSlice(ev.detail.value);
    }

    onMount(() => {
        showSnap = snap != undefined;
    });
</script>

<slice-picker style="height: {imgHeight}px; width: {imgWidth}px">
    <slice-picker-img style="height: {imgHeight}px; width: {imgWidth}px">
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <img
            class="image"
            style="max-width: 256px; width: inherit; height: inherit; object-fit: cover; object-position: 100% {showFullImage
                ? 0
                : (-$roi?.top / cameraImageWidth) * imgWidth}px; "
            id="slicePicture"
            bind:this={img}
            on:load={handleImageLoad}
            on:error={handleImageLoadError}
            on:click={selectSlice}
            alt=""
            src={getScanThumbnailUrl(tomography)} />
    </slice-picker-img>
    <slice-picker-overlay>
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <svg
            on:click={selectSlice}
            version="1.1"
            id="sliceLines"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            x="0px"
            y="0px"
            width="{imgWidth}px"
            height="{imgHeight}px"
            xml:space="preserve">
            {#if showFullImage && isNumeric($finalFdkSettings?.min_slice) && isNumeric($finalFdkSettings?.max_slice)}
                <rect
                    class="sliceRangeFog"
                    x="0"
                    y="0"
                    width="256"
                    height={scaleSliceIndex($finalFdkSettings?.min_slice) || 0} />
                <rect
                    class="sliceRangeFog"
                    x="0"
                    y={scaleSliceIndex($finalFdkSettings?.max_slice) || 256}
                    width="256"
                    height={scaleSliceIndex($roi.bot) - scaleSliceIndex($finalFdkSettings?.max_slice) || 0} />
                <path
                    class="sliceRangePath"
                    stroke-width="2"
                    d="M0 {scaleSliceIndex($finalFdkSettings?.min_slice)} l256 0" />
                <path
                    class="sliceRangePath"
                    stroke-width="2"
                    d="M0 {scaleSliceIndex($finalFdkSettings?.max_slice)} l256 0" />
            {/if}
            {#each centershiftSlices as cs}
                <path
                    class="centershiftPath"
                    stroke-width="3"
                    d="M0 {scaleSliceIndex(cs)} l256 0" />
            {/each}
            {#each beamhardCuppingSlices as bhs}
                <path
                    class="beamhardCuppingPath"
                    stroke-width="3"
                    d="M0 {scaleSliceIndex(bhs)} l256 0" />
            {/each}
        </svg>
    </slice-picker-overlay>
    {#if isNumeric(sliceIndex)}
        <RangeSlider
            --width="{imgWidth}px"
            id="sliceslider"
            values={tmpSliceIndex}
            min={0}
            max={$roi?.bot - $roi?.top}
            vertical
            reversed
            pips
            pipstep={500}
            {step}
            float
            springValues={{stiffness: 1, damping:1}}
            first="label"
            last="label"
            on:change={onSliceSliderChange}
            on:stop={onSliceSliderStop} />
        <br />
        <div>
            {#if showSnap}
                <span id="slicesnap">Pick new<Switch bind:checked={snap} />Snap to nearest</span>
            {/if}
        </div>
    {/if}
    <!-- DEBUG
    <annotation>min_slice: {$finalFdkSettings?.min_slice}, max_slice: {$finalFdkSettings?.max_slice}</annotation><br>
    <annotation>$roi: {$roi?.top}..{$roi?.bot}</annotation><br>
    <annotation>cameraRoi: {tomography?.command?.camera?.roi_px?.top}.. {tomography?.command?.camera?.roi_px?.bot}</annotation><br>
    <annotation>cameraImageWidth: {cameraImageWidth}</annotation><br>
    <annotation>sliceIndex: {sliceIndex}</annotation>
    -->
</slice-picker>

<style>
    slice-picker {
        background-color: aliceblue;
        max-width: 256px;
        margin-bottom: 8px;
        position: relative;
    }

    :global(body.dark) slice-picker {
        background-color: steelblue;
    }

    slice-picker-img {
        max-width: 256px;
        display: inline-block;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        overflow: hidden;
        text-align: center;
    }

    slice-picker-overlay {
        width: 100%;
        height: 100%;
        cursor: pointer;
    }

    #sliceLines {
        position: absolute;
        transform-origin: center;
    }

    :global(#slicesnap) {
        margin: 8px auto 8px auto;
        display: block;
        width: 256px;
    }

    :global(#slicesnap .switch) {
        margin-right: 4px;
        margin-left: 4px;
    }

    .centershiftPath {
        stroke-dashoffset: 0;
        stroke-dasharray: 10;
        stroke: red;
        opacity: 30%;
    }

    .sliceRangePath {
        stroke: blue;
        opacity: 30%;
    }

    .sliceRangeFog {
        fill: black;
        opacity: 30%;
    }

    .beamhardCuppingPath {
        stroke-dashoffset: 10;
        stroke-dasharray: 10;
        stroke: orange;
        opacity: 30%;
    }

    :global(#sliceslider) {
        min-height: 20px;
        height: 100%;
        margin: 0;
        --range-slider: none;
        --pip-text: purple;
        --pip-hover-text: yellow;
        --pip: purple;
        --pip-hover: yellow;
        opacity: 50%;
        cursor: pointer;
    }

    /*.rangePips .pip {color: red}*/

    :global(#sliceslider .rangeHandle) {
        width: var(--width);
        height: 0.5em;
        transform: translateY(-50%) translateX(-1.5%);
    }

    :global(#slicesnap) {
        margin: 8px auto 8px auto;
    }

    :global(#slicesnap .switch) {
        margin-right: 4px;
        margin-left: 4px;
    }
</style>
